/**
 * Background Logger - Renderer Proxy
 * This file is safe for use in the React Renderer process.
 * It sends all log events via IPC to the Electron Main process.
 */

import { LOG_CHANNELS } from './ipc';
import type { CriticalEventType, POSLogEvent, APILogEvent } from './config';

function desktopApi(): { invoke: (...args: any[]) => Promise<any> } | null {
    if (typeof window === 'undefined') return null;
    const api = (window as any).api;
    if (!api || typeof api.invoke !== 'function') return null;
    return api;
}

export async function logPOSEvent(eventType: string, data: any): Promise<void> {
    const api = desktopApi();
    if (!api) return;
    try {
        await api.invoke(LOG_CHANNELS.LOG_POS_EVENT, eventType, data);
    } catch (error) {
        console.error('[BackgroundLogger] Failed to write POS event:', eventType, error);
    }
}

export async function logAPIResponse(endpoint: string, method: string, request: any, response: any, duration: number, status?: number): Promise<void> {
    const api = desktopApi();
    if (!api) return;
    try {
        await api.invoke(LOG_CHANNELS.LOG_API_RESPONSE, [endpoint, method, request, response, duration, status]);
    } catch (error) {
        console.error('[BackgroundLogger] Failed to write API event:', endpoint, error);
    }
}

export async function logCriticalEvent(eventType: CriticalEventType, data: any): Promise<void> {
    const api = desktopApi();
    if (!api) return;
    try {
        await api.invoke(LOG_CHANNELS.LOG_CRITICAL_EVENT, eventType, data);
    } catch (error) {
        console.error('[BackgroundLogger] Failed to write critical event:', eventType, error);
    }
}

export async function flushLogs(): Promise<void> {
    const api = desktopApi();
    if (!api) return;
    try {
        await api.invoke(LOG_CHANNELS.FLUSH);
    } catch (error) {
        console.error('[BackgroundLogger] Failed to flush logs:', error);
    }
}

// Stubs for API compatibility
export async function initializeLogger(): Promise<void> { }
export async function shutdownLogger(): Promise<void> { }

export type { CriticalEventType, POSLogEvent, APILogEvent } from './config';
